import React, {useEffect, useState} from "react";
import {useQueryClient} from "@tanstack/react-query";
import {GridColDef} from "@mui/x-data-grid";
import {Theme} from "@mui/material/styles";
import {createStyles, makeStyles} from "@mui/styles";
import {UserDto} from "../../../../../common/apis/user/UserDto";
import userApiClient from "../../../../../common/apis/user/UserApiClient";
import userGroupApiClient from "../../../../../common/apis/usergroup/UserGroupApiClient";
import Dialog from "../../../../../components/dialogs/Dialog";
import DialogTitle from "../../../../../components/dialogs/DialogTitle";
import DialogContent from "../../../../../components/dialogs/DialogContent";
import DialogActions from "../../../../../components/dialogs/DialogActions";
import Grid from "../../../../../components/dialogs/Grid";
import ExtGridWrapper from "../../../../../components/grid/ExtGridWrapper";
import {CancelButton} from "../../../../../components/button/CancelButton";
import {Button} from "../../../../../components/button/Button";
import {ButtonLayout, NegativeButtons, PositiveButtons} from "../../../../../components/button/ButtonLayout";
import {t} from "../../../../../store/localization/TranslMessasge";
import {CommonTranslationKey} from "../../CommonTranslationKey";
import {ErrorTranslationKey} from "../../ErrorTranslationKey";
import {UserGroupsTranslationKey} from "../UserGroupsTranslationKey";
import {invalidateFindAllUserGroupMembersQueryCache, useFindAllUserGroupMembersQuery} from "../UserGroupQuery";
import {PersistentStateId} from "../../../../../store/common/Grid";
import Snackbar from "../../snackbar/Snackbar";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        grid: {
            minHeight: "18em",
        }
    })
);

interface UserGroupAddMemberDialogProps {
    open: boolean;
    groupId: string;
    onClose: () => void;
}

export default function UserGroupMemberAddDialog({open, groupId, onClose}: Readonly<UserGroupAddMemberDialogProps>) {
    const classes = useStyles();
    const queryClient = useQueryClient();
    const {data: members} = useFindAllUserGroupMembersQuery(groupId);

    const [allUsers, setAllUsers] = useState<UserDto[]>([]);
    const [selectedUserLogins, setSelectedUserLogins] = useState<string[]>([]);
    const [gridResourceId, setGridResourceId] = useState<string>(`userGroupAddMemberDialogUsers_${Date.now()}`);
    const [inProgress, setInProgress] = useState(false);

    useEffect(() => {
        userApiClient.getAllUsers()
            .then(users => setAllUsers(users))
            .catch(() => Snackbar.error(t(ErrorTranslationKey.UNEXPECTED_ERROR_OCCURRED)));
    }, []);

    useEffect(() => {
        if (!open) {
            setSelectedUserLogins([]);
            setInProgress(false);
            setGridResourceId(`userGroupAddMemberDialogUsers_${Date.now()}`);

        }
    }, [open]);

    const memberLogins = new Set((members ?? []).map(m => m.login));
    const availableUsers = allUsers.filter(user => !memberLogins.has(user.login));

    const columns: GridColDef<UserDto>[] = [
        {field: 'login', headerName: t(UserGroupsTranslationKey.LOGIN), width: 200},
        {field: 'firstName', headerName: t(UserGroupsTranslationKey.FIRST_NAME), width: 200},
        {field: 'lastName', headerName: t(UserGroupsTranslationKey.LAST_NAME), width: 200},
        {field: 'email', headerName: t(UserGroupsTranslationKey.EMAIL), width: 250},
    ];

    function addMembers() {
        setInProgress(true);
        Promise.all(selectedUserLogins.map(login => userGroupApiClient.addMember(groupId, login)))
            .then(() => {
                invalidateFindAllUserGroupMembersQueryCache(groupId, queryClient);
                Snackbar.success(t(CommonTranslationKey.DATA_SAVED_SUCCESSFULLY));
                onClose();
            })
            .catch((err) => {
                Snackbar.error(t(ErrorTranslationKey.UNEXPECTED_ERROR_OCCURRED), err);
            })
            .finally(() => setInProgress(false));
    }

    return (
            <Dialog open={open} maxWidth="md" onClose={onClose}>
                <DialogTitle title={t(UserGroupsTranslationKey.ADD_MEMBER)} onDialogClosed={onClose}/>
                <DialogContent>
                    <Grid container>
                        <Grid item xs={12}>
                            <ExtGridWrapper
                                    columns={columns}
                                    rows={availableUsers}
                                    rowCount={availableUsers.length}
                                    getRowId={row => row.login}
                                    dataGridClass={classes.grid}
                                    onSelectionChanged={(selectedRowIds) => setSelectedUserLogins(selectedRowIds as string[])}
                                    peristentStateId={PersistentStateId.USER_GROUP_ADD_MEMBER_DIALOG_GRID}
                                    resourceId={gridResourceId}
                                    bulkActionsOpenedPermanently={true}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <ButtonLayout>
                        <NegativeButtons>
                            <CancelButton onClick={onClose} disabled={inProgress}/>
                        </NegativeButtons>
                        <PositiveButtons>
                            <Button
                                    label={t(CommonTranslationKey.ADD)}
                                    variant="contained"
                                    onClick={addMembers}
                                    disabled={inProgress || selectedUserLogins.length === 0}
                            />
                        </PositiveButtons>
                    </ButtonLayout>
                </DialogActions>
            </Dialog>
    );
}
